// Generic "confirm + reason" dialog used by Cancel, Manual-complete and Issue on the DN detail.
// Same shape: title, description, mandatory reason (>= 3 chars), optional photo evidence, confirm
// button. The caller wires the network call via onConfirm(reason, photoUrls) returning a Promise.
// The close button reads «Болих» so it never repeats the confirm label (O93).

import { useState } from "react"
import { ActionIcon, Alert, Box, Button, ButtonProps, FileButton, Group, Image, Loader, Modal, Stack, Text, Textarea } from "@mantine/core"
import { PlusIcon, XIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { MediaApi } from "../../apis"
import { message } from "../../utils/message"
import { translateBackendError } from "../delivery/translate-error"

type Props = {
  opened: boolean
  onClose: () => void
  title: string
  description?: string
  confirmLabel: string
  confirmColor?: ButtonProps["color"]
  reasonLabel?: string
  reasonPlaceholder?: string
  /** When true, show an optional image-evidence uploader; the URLs are the 2nd arg of onConfirm. */
  withPhotos?: boolean
  onConfirm: (reason: string, photoUrls: string[]) => Promise<void>
  onSucceeded?: () => void
}

const MAX_REASON = 2000
const MAX_PHOTOS = 6

export function ReasonDialog({
  opened,
  onClose,
  title,
  description,
  confirmLabel,
  confirmColor = "brand",
  reasonLabel,
  reasonPlaceholder,
  withPhotos = false,
  onConfirm,
  onSucceeded,
}: Props) {
  const { t } = useTranslation(["delivery", "common"])
  const [reason, setReason] = useState("")
  const [photoUrls, setPhotoUrls] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const trimmed = reason.trim()
  const isValid = trimmed.length >= 3

  const reset = () => {
    setReason("")
    setPhotoUrls([])
    setError(null)
  }

  const handleClose = () => {
    if (submitting || uploading) return
    reset()
    onClose()
  }

  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return
    setUploading(true)
    setError(null)
    try {
      const room = MAX_PHOTOS - photoUrls.length
      const picked = files.slice(0, Math.max(0, room))
      const uploaded: string[] = []
      for (const file of picked) {
        const form = new FormData()
        form.append("file", file)
        const res = await MediaApi.upload(form)
        const url = typeof res === "string" ? res : res?.url
        if (url) uploaded.push(url)
      }
      setPhotoUrls((prev) => [...prev, ...uploaded])
    } catch (err) {
      message.error(translateBackendError(err))
    } finally {
      setUploading(false)
    }
  }

  const removePhoto = (url: string) => setPhotoUrls((prev) => prev.filter((u) => u !== url))

  const handleConfirm = async () => {
    if (!isValid) return
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm(trimmed, photoUrls)
      message.success(t("common:messages.requestSuccess"))
      onSucceeded?.()
      reset()
      onClose()
    } catch (err) {
      setError(translateBackendError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={title} centered size="md">
      <Stack gap="md">
        {description && (
          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
            {description}
          </Text>
        )}
        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <Textarea
          label={reasonLabel ?? t("delivery.reason.label")}
          description={t("delivery.reason.hint")}
          placeholder={reasonPlaceholder ?? t("delivery.reason.placeholder")}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={3}
          autosize
          required
          maxLength={MAX_REASON}
          error={reason.length > 0 && trimmed.length < 3 ? t("delivery.reason.tooShort") : undefined}
        />

        {withPhotos && (
          <Box>
            <Text fz="var(--nw-fz-body)" fw={500} mb={6}>
              {t("delivery.reason.photos")}{" "}
              <Text span fz="var(--nw-fz-body)" c="var(--nw-text-3)" fw={400}>
                {t("delivery.reason.photosOptional", { count: photoUrls.length, max: MAX_PHOTOS })}
              </Text>
            </Text>
            <Group gap="xs">
              {photoUrls.map((url) => (
                <Box key={url} pos="relative" w={72} h={72}>
                  <Image src={url} w={72} h={72} radius="sm" fit="cover" alt={t("delivery.reason.photoAlt")} />
                  <ActionIcon
                    size="sm"
                    color="gray"
                    variant="filled"
                    radius="xl"
                    pos="absolute"
                    top={-6}
                    right={-6}
                    onClick={() => removePhoto(url)}
                    aria-label={t("delivery.reason.photoRemove")}
                  >
                    <XIcon size={12} weight="bold" />
                  </ActionIcon>
                </Box>
              ))}
              {photoUrls.length < MAX_PHOTOS && (
                <FileButton onChange={handleFiles} accept="image/png,image/jpeg,image/webp" multiple>
                  {(props) => (
                    <Button {...props} variant="default" w={72} h={72} p={0} disabled={uploading} aria-label={t("delivery.reason.photoAdd")}>
                      {uploading ? <Loader size="sm" /> : <PlusIcon size={20} />}
                    </Button>
                  )}
                </FileButton>
              )}
            </Group>
          </Box>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose} disabled={submitting || uploading}>
            {t("common:actions.dismiss")}
          </Button>
          <Button color={confirmColor} onClick={handleConfirm} disabled={!isValid || uploading} loading={submitting}>
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
